import React, { useState, useEffect } from 'react';
import { Database, Plus, RefreshCw, Trash2, Edit2, CheckCircle, Loader2, Network } from 'lucide-react';
import { toast } from 'sonner';
import { DatabaseConnection, TableDescription } from '../../types';
import DatabaseConnectionForm from './DatabaseConnectionForm';
import ERDDiagram from '../ERD/ERDDiagram';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { ConnectionsLoadingSkeleton } from '../LoadingStates';
import { useDatabaseConnection } from '@/contexts/DatabaseConnectionContext';
import { useTableScanStatus } from '@/hooks/useTableScanStatus';
import { useTabNavigation } from '@/contexts/AppStateContext';

const DatabaseConnections: React.FC = () => {
  // Use centralized state management
  const {
    connections,
    connectionsLoading,
    connectionsError,
    loadConnections,
    createConnection,
    updateConnection,
    deleteConnection,
    triggerTableScan,
  } = useDatabaseConnection();

  // Navigation state
  const { navigateToTab, completeTabTransition } = useTabNavigation();

  // Local form state
  const [showForm, setShowForm] = useState(false);
  const [editingConnection, setEditingConnection] = useState<DatabaseConnection | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [erdConnectionId, setErdConnectionId] = useState<string | null>(null);
  const [erdSchema, setErdSchema] = useState<string | undefined>(undefined);

  // Register navigation tab (connections already loaded by context on mount)
  useEffect(() => {
    navigateToTab('connections');
  }, [navigateToTab]);

  const handleSyncSchema = async (connectionId: string) => {
    setSyncingId(connectionId);
    try {
      await triggerTableScan(connectionId);
      toast.success('Schema synchronization started');
    } catch (error) {
      toast.error('Failed to sync schema');
    } finally {
      setSyncingId(null);
    }
  };

  const handleViewERD = (connection: DatabaseConnection) => {
    setErdConnectionId(connection.id!);
    // Use the first schema if multiple schemas are available
    setErdSchema(connection.schemas && connection.schemas.length > 0 ? connection.schemas[0] : undefined);
  };

  const handleCloseERD = () => {
    setErdConnectionId(null);
    setErdSchema(undefined);
  };

  const handleDelete = async (connectionId: string) => {
    const connection = connections.find(c => c.id === connectionId);
    if (!connection) return;

    if (!window.confirm(`Are you sure you want to delete the database connection "${connection.alias}"?\n\nThis will also remove all associated table descriptions, golden SQLs, and prompts.`)) {
      return;
    }

    try {
      await deleteConnection(connectionId);
      // Success toast is handled by the context
    } catch (error: any) {
      // Error toast is handled by the context
      console.error('Delete failed:', error);
    }
  };

  const handleEdit = (connection: DatabaseConnection) => {
    setEditingConnection(connection);
    setShowForm(true);
  };


  const handleFormClose = async (newConnectionId?: string) => {
    setShowForm(false);
    setEditingConnection(null);

    // Refresh connections to get the latest data
    await loadConnections(true); // force refresh

    if (newConnectionId) {
      // Trigger table scan for the new connection
      await triggerTableScan(newConnectionId);
    }
  };

  const getDatabaseTypeLabel = (uri: string): string => {
    if (!uri) return 'Unknown';
    if (uri.includes('postgresql')) return 'PostgreSQL';
    if (uri.includes('mysql')) return 'MySQL';
    if (uri.includes('sqlserver')) return 'SQL Server';
    if (uri.includes('bigquery')) return 'BigQuery';
    if (uri.includes('snowflake')) return 'Snowflake';
    if (uri.includes('databricks')) return 'Databricks';
    if (uri.includes('clickhouse')) return 'ClickHouse';
    if (uri.includes('athena')) return 'AWS Athena';
    if (uri.includes('duckdb')) return 'DuckDB';
    if (uri.includes('sqlite')) return 'SQLite';
    if (uri.includes('redshift')) return 'Redshift';
    return 'Database';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="border-b border-[#e6e6ea] px-5 py-3 shrink-0">
        <div className="flex items-center justify-between">
          <h1 className="text-[18px] font-semibold text-[#22212d]">Database Connections</h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadConnections(true)}
              disabled={connectionsLoading}
              className="h-7 px-2.5 text-[13px] font-medium border-[#d8d8de]"
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${connectionsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={() => setShowForm(true)}
              variant="gradient"
              size="sm"
              className="h-7 px-2.5 text-[13px]"
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add Connection
            </Button>
          </div>
        </div>
      </div>

      {/* Page Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-6">
        <Card className="bg-white border-[#e6e6ea]">
        <CardHeader className="pb-4">
          <CardTitle className="text-[16px] font-semibold text-[#22212d]">
            All Connections
          </CardTitle>
          <CardDescription className="text-[13px] text-[rgba(34,33,45,0.6)]">
            Manage your database connections and sync schemas
          </CardDescription>
        </CardHeader>

        <CardContent>
          {/* Show error if any */}
          {connectionsError && (
            <Alert className="mb-4 border-destructive/50 bg-destructive/10">
              <AlertDescription className="text-[13px] text-destructive">{connectionsError}</AlertDescription>
            </Alert>
          )}

          {connectionsLoading && connections.length === 0 ? (
            <ConnectionsLoadingSkeleton />
          ) : connections.length === 0 ? (
            <div className="text-center py-12">
              <Database className="h-12 w-12 text-[rgba(34,33,45,0.5)] mx-auto mb-4" />
              <p className="text-[14px] text-[rgba(34,33,45,0.6)] mb-4">No database connections found</p>
              <Button onClick={() => setShowForm(true)} variant="gradient">
                Add Your First Connection
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {connections.map((connection) => (
                <Card key={connection.id} className="hover:shadow-md transition-shadow bg-white border-[#e6e6ea]">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Database className="h-5 w-5 text-primary" />
                          <h3 className="text-[16px] font-semibold text-[#22212d]">{connection.alias}</h3>
                          <Badge variant="secondary" className="text-[12px] bg-[#f3f3f6] text-[#22212d]">
                            {getDatabaseTypeLabel(connection.connection_uri || '')}
                          </Badge>
                        </div>
                        {connection.database && typeof connection.database === 'string' && (
                          <p className="text-[13px] text-[rgba(34,33,45,0.6)] mb-1">
                            Database: <span className="font-medium text-[#22212d]">{connection.database}</span>
                          </p>
                        )}
                        {connection.schemas && connection.schemas.length > 0 && (
                          <p className="text-[13px] text-[rgba(34,33,45,0.6)] mb-1">
                            Schemas: <span className="font-medium text-[#22212d]">{connection.schemas.join(', ')}</span>
                          </p>
                        )}
                        <p className="text-[12px] text-[rgba(34,33,45,0.5)]">
                          Created: {new Date(connection.created_at || '').toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewERD(connection)}
                          className="h-8 w-8 p-0"
                          title="View ERD Diagram"
                        >
                          <Network className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSyncSchema(connection.id!)}
                          disabled={syncingId === connection.id}
                          className="h-8 w-8 p-0"
                        >
                          <RefreshCw className={`h-4 w-4 ${syncingId === connection.id ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(connection)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(connection.id!)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </div>

      {showForm && (
        <DatabaseConnectionForm
          connection={editingConnection}
          onClose={handleFormClose}
        />
      )}

      {/* ERD Modal */}
      {erdConnectionId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-2">
                <Network className="h-5 w-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Entity Relationship Diagram
                </h2>
                {erdSchema && (
                  <Badge variant="secondary" className="text-xs">
                    Schema: {erdSchema}
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCloseERD}
                className="h-8 w-8 p-0"
              >
                <span className="text-xl">&times;</span>
              </Button>
            </div>
            <div className="flex-1 overflow-hidden">
              <ERDDiagram dbConnectionId={erdConnectionId} schema={erdSchema} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseConnections;